import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import EmailVerifyBanner from './EmailVerifyBanner';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  LayoutDashboard, 
  Upload, 
  LogOut, 
  User,
  Menu,
  X,
  ChevronDown,
  Shield,
  TrendingUp,
  Users,
  Swords,
  Lock,
  CreditCard,
  Sparkles,
  FileText,
  ListTodo,
  Mail
} from 'lucide-react';

export default function Layout({ children }) {
  const { user, logout, updateRole, isManager } = useAuth();
  const { subscription, plan, planName, hasFeature, getUsage } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingTasks, setPendingTasks] = useState(0);
  const [pendingInvites, setPendingInvites] = useState([]);

  useEffect(() => {
    const fetchPending = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/summary`, { credentials: 'include' });
        if (res.ok) {
          const d = await res.json();
          setPendingTasks((d.todo || 0) + (d.in_progress || 0));
        }
      } catch (e) {}
    };
    const fetchInvites = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invitations/pending`, { credentials: 'include' });
        if (res.ok) setPendingInvites(await res.json());
      } catch (e) {}
    };
    fetchPending();
    fetchInvites();
    const interval = setInterval(() => { fetchPending(); fetchInvites(); }, 30000);
    return () => clearInterval(interval);
  }, []);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Upload Call', href: '/upload', icon: Upload },
    { name: 'Pipeline', href: '/pipeline', icon: TrendingUp, feature: 'pipeline' },
    { name: 'Practice', href: '/practice', icon: Swords },
    { name: 'Deal Advisor', href: '/advisor', icon: Sparkles, feature: 'deal_advisor', managerOnly: true },
    { name: 'Script Builder', href: '/scripts', icon: FileText },
    { name: 'Tasks', href: '/tasks', icon: ListTodo },
    { name: 'Team', href: '/team', icon: Users, feature: 'team_dashboard', managerOnly: true },
  ].filter(item => !item.managerOnly || isManager);

  const callUsage = getUsage('call_analyses');
  const voiceUsage = getUsage('voice_standard_minutes');

  const isActive = (href) => location.pathname === href;

  const handleRoleSwitch = async () => {
    const newRole = isManager ? 'sales_rep' : 'manager';
    await updateRole(newRole);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar - Desktop: Rolex Green Glassmorphism */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 glass-sidebar shadow-sidebar z-30">
        {/* Logo */}
        <div className="flex items-center px-6 h-16 border-b border-white/10">
          <img src="/ludy-wordmark.png" alt="Ludy" className="h-9 scale-110 origin-left" style={{ filter: 'brightness(0) saturate(100%) invert(76%) sepia(30%) saturate(700%) hue-rotate(5deg) brightness(95%) contrast(90%)' }} />
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1.5">
          {/* Pending invitation banner (for reps who were invited to a team) */}
          {pendingInvites.length > 0 && (
            <div className="mb-3 space-y-1.5" data-testid="sidebar-pending-invites">
              {pendingInvites.slice(0, 2).map(inv => (
                <Link
                  key={inv.invite_id}
                  to={`/invite/${inv.token}`}
                  className="relative flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-[#C9A84C]/15 border border-[#C9A84C]/30 hover:bg-[#C9A84C]/25 transition-colors group"
                  data-testid={`sidebar-invite-${inv.invite_id}`}
                >
                  <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#C9A84C] animate-pulse" />
                  <div className="w-7 h-7 rounded-full bg-[#C9A84C]/30 flex items-center justify-center shrink-0">
                    <Mail className="w-3.5 h-3.5 text-[#C9A84C]" />
                  </div>
                  <div className="flex-1 min-w-0 pr-3">
                    <p className="text-[11px] font-semibold text-[#C9A84C] uppercase tracking-wider leading-none">
                      Team invitation
                    </p>
                    <p className="text-xs text-white/90 font-medium truncate mt-1">
                      {inv.team_name}
                    </p>
                    <p className="text-[10px] text-white/60 truncate">
                      from {inv.inviter_name}
                    </p>
                  </div>
                </Link>
              ))}
              {pendingInvites.length > 2 && (
                <p className="text-[10px] text-white/50 text-center">
                  +{pendingInvites.length - 2} more
                </p>
              )}
            </div>
          )}

          {navigation.map((item) => {
            const locked = item.feature && !hasFeature(item.feature);
            return (
              <Link
                key={item.name}
                to={locked ? '/pricing' : item.href}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                  locked
                    ? 'text-white/30 hover:text-white/50 hover:bg-white/5'
                    : isActive(item.href)
                      ? 'bg-white/20 text-white font-medium shadow-sm'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm flex-1">{item.name}</span>
                {item.name === 'Tasks' && pendingTasks > 0 && (
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                    {pendingTasks > 9 ? '9+' : pendingTasks}
                  </span>
                )}
                {locked && <Lock className="w-3.5 h-3.5" />}
              </Link>
            );
          })}
        </nav>

        {/* Usage meters */}
        {subscription && (
          <div className="px-4 pb-3">
            <div className="p-3 rounded-lg bg-white/10 border border-white/10 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium text-white/60 uppercase tracking-wider">
                  {planName} Plan
                </span>
                <Link to="/pricing" className="text-[10px] text-white/80 hover:text-white hover:underline" data-testid="upgrade-link">
                  {plan === 'free' ? 'Upgrade' : 'Plans'}
                </Link>
              </div>
              <div>
                <div className="flex justify-between text-[10px] mb-1">
                  <span className="text-white/50">Calls</span>
                  <span className="text-white/70">
                    {callUsage.used}/{callUsage.limit === -1 ? '\u221e' : callUsage.limit}
                  </span>
                </div>
                <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      callUsage.percentage >= 90 ? 'bg-red-400' : callUsage.percentage >= 70 ? 'bg-yellow-400' : 'bg-white/70'
                    }`}
                    style={{ width: `${callUsage.limit === -1 ? 5 : callUsage.percentage}%` }}
                  />
                </div>
              </div>
              {(voiceUsage.limit > 0 || voiceUsage.limit === -1) && (
                <div>
                  <div className="flex justify-between text-[10px] mb-1">
                    <span className="text-white/50">Voice</span>
                    <span className="text-white/70">
                      {voiceUsage.used}m/{voiceUsage.limit === -1 ? '\u221e' : voiceUsage.limit + 'm'}
                    </span>
                  </div>
                  <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        voiceUsage.percentage >= 90 ? 'bg-red-400' : voiceUsage.percentage >= 70 ? 'bg-yellow-400' : 'bg-white/70'
                      }`}
                      style={{ width: `${voiceUsage.limit === -1 ? 5 : voiceUsage.percentage}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* User section */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2">
            <Avatar className="w-10 h-10 border border-white/20">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-white/15 text-white text-sm">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.name}
              </p>
              <p className="text-xs text-white/50 capitalize">
                {user?.role?.replace('_', ' ')}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="lg:pl-64">
        {/* Top navbar - light glassmorphism */}
        <header className="sticky top-0 z-40 h-16 bg-white/70 backdrop-blur-md border-b border-border/50">
          <div className="flex items-center justify-between h-full px-4 lg:px-8">
            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 text-muted-foreground hover:text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            {/* Mobile logo */}
            <div className="lg:hidden flex items-center">
              <img src="/ludy-wordmark.png" alt="Ludy" className="h-5" />
            </div>

            {/* Role badge & user menu */}
            <div className="flex items-center gap-4 ml-auto">
              {/* Role badge */}
              <button
                onClick={handleRoleSwitch}
                className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  isManager
                    ? 'bg-primary/10 text-primary border border-primary/20'
                    : 'bg-secondary text-muted-foreground border border-border'
                }`}
                data-testid="role-switch-btn"
              >
                <Shield className="w-3.5 h-3.5" />
                {isManager ? 'Manager View' : 'Rep View'}
              </button>

              {/* User dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="user-menu-btn">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white/90 backdrop-blur-md border-border/50">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-foreground">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-border/50" />
                  <DropdownMenuItem 
                    className="cursor-pointer"
                    onClick={handleRoleSwitch}
                    data-testid="dropdown-role-switch"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Switch to {isManager ? 'Rep' : 'Manager'} View
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="cursor-pointer"
                    onClick={() => navigate('/profile')}
                    data-testid="dropdown-profile"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Profile & Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="cursor-pointer"
                    onClick={() => navigate('/pricing')}
                    data-testid="dropdown-pricing"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Plans & Pricing
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-border/50" />
                  <DropdownMenuItem 
                    className="cursor-pointer text-destructive focus:text-destructive"
                    onClick={logout}
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Mobile navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-30 bg-white/95 backdrop-blur-sm pt-16">
            <nav className="p-4 space-y-2">
              {navigation.map((item) => {
                const locked = item.feature && !hasFeature(item.feature);
                return (
                  <Link
                    key={item.name}
                    to={locked ? '/pricing' : item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      locked
                        ? 'text-muted-foreground/40'
                        : isActive(item.href)
                          ? 'bg-primary/10 text-primary border border-primary/20'
                          : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium flex-1">{item.name}</span>
                    {locked && <Lock className="w-3.5 h-3.5" />}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}

        {/* Unverified email nudge — no-op when user.email_verified is true */}
        <EmailVerifyBanner />

        {/* Page content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
